/* Copyright (c) 2020-2024 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useMemo, useState } from "react";
import { When } from "react-if";
import {
  BrickLoader,
  Button,
  FlexContainer,
  InputSearch,
  theme,
} from "@nordcloud/gnui";
import { SearchV2SortField } from "~/generated/graphql";
import {
  ErrorWrap,
  LoaderWrap,
  Pagination,
  ReactTableV2,
  SortDirection,
} from "~/components";
import { EstatePeriod } from "~/constants";
import {
  useCurrency,
  useEstateRecordsV2,
  useEstateSortV2,
  estateSearchJobFields,
  useFilteredQueryState,
  useQueryState,
} from "~/hooks";
import { useBillingPeriod } from "~/services/customers";
import { showError } from "~/services/toast";
import { SearchSortOrder, isNotEmpty, isNotNil } from "~/tools";
import { AccumulatedCostData } from "~/views/estate/components/Charts/AccumulatedCostData";
import { EstateQueryState } from "~/views/estate/components/Charts/hooks/constants";
import { FiltersSidebar } from "~/views/estate/components/FiltersSidebar";
import { filterWasteFilter } from "~/views/estate/components/helpers";
import {
  useEstateFilters,
  useQueryParametersValidation,
} from "~/views/estate/components/hooks";
import { convertTags } from "~/views/estate/helpers";
import { CloudProviderType, Provider } from "../../types";
import { EstateContainer } from "./EstateContainer";
import { EstateListColumns } from "./EstateList/EstateListColumns";
import { generateNewRowData } from "./EstateList/helpers";
import { EstateRecordsNotification } from "./EstateRecordsNotification";
import { SearchBarContainer } from "./SearchBar";

type Props = {
  providerId: string;
  providerType?: CloudProviderType;
  provider: Provider;
};

export function EstateRecords({ provider, providerType, providerId }: Props) {
  const { setQueryState } = useQueryState<{
    field: SearchV2SortField;
    order: SearchSortOrder;
  }>();

  const { currency } = useCurrency();
  const { isCurrentBillingPeriod } = useBillingPeriod();
  const { preSelectedMonth } = useQueryParametersValidation();
  const {
    filters,
    isSidebarOpen,
    handleUpdate,
    clearFilters,
    toggleFilters,
    toggleIsSidebarOpen,
  } = useEstateFilters();

  const { state, updateQueryState } = useFilteredQueryState(
    estateSearchJobFields
  );

  // @ts-expect-error ignored temporary after migration to TypeScript 4.9
  const { page, limit, field, order, query, ...restFilters }: EstateQueryState =
    state;

  const { sortField, sortDirection, onSortSelect, onSortOrderChange } =
    // @ts-expect-error ignored temporary after migration to TypeScript 4.9
    useEstateSortV2({ field, order, updateQueryState });

  const estateFilters = {
    filter: {
      ...filterWasteFilter(restFilters),
      metadata: convertTags(restFilters.metadata),
      tags: convertTags(restFilters.tags),
    },
    query,
    sort: {
      field: sortField ?? SearchV2SortField.Cost,
      order: sortDirection ?? SearchSortOrder.Dsc,
    },
  };

  const { data, loading, error } = useEstateRecordsV2(
    {
      variables: {
        page,
        limit,
        query,
        filter: {
          account: [providerId],
          ...estateFilters.filter,
        },
        sort: {
          field: sortField ?? SearchV2SortField.Cost,
          order: sortDirection ?? SearchSortOrder.Dsc,
        },
      },
    },
    showError,
    preSelectedMonth.from
  );

  const [searchKey, setSearchKey] = useState(query ?? "");

  const rows = useMemo(
    () =>
      data?.resources?.map((res) => ({
        ...generateNewRowData(res, currency),
      })) ?? [],
    [data, currency]
  );

  const estateRecords = data?.resources ?? [];

  const hasData = isNotEmpty(estateRecords);
  const count = data?.count ?? 0;

  const handleSorting = ({ id, desc }: { id: string; desc: boolean }) => {
    if (id !== sortField) {
      onSortSelect(id as SearchV2SortField);

      return;
    }

    onSortOrderChange(
      desc ? SortDirection.Descending : SortDirection.Ascending
    );
  };

  const defaultSort = {
    id: sortField ?? "Total Cost",
    desc: isNotNil(sortDirection)
      ? sortDirection === SearchSortOrder.Dsc
      : true,
  };

  const columns = useMemo(() => EstateListColumns(), []);
  const period = isCurrentBillingPeriod
    ? EstatePeriod.DEFAULT_MONTH
    : EstatePeriod.PREVIOUS_MONTH;

  useEffect(() => {
    handleUpdate({ provider: [provider], account: [providerId] });
  }, [providerId, provider]);

  return (
    <>
      <EstateContainer>
        <ErrorWrap error={error}>
          <LoaderWrap loading={loading} Component={BrickLoader}>
            <FlexContainer
              direction="column"
              alignItems="flex-start"
              gap={theme.spacing.spacing04}
            >
              <When condition={hasData}>
                <EstateRecordsNotification
                  provider={provider}
                  providerType={providerType}
                />
              </When>
              <SearchBarContainer>
                <div css={{ width: "100%" }}>
                  <InputSearch
                    value={searchKey}
                    onChange={(event) => setSearchKey(event.target.value)}
                    onSearch={() =>
                      setQueryState({
                        ...state,
                        query: searchKey,
                        page: 0,
                        field: sortField,
                        order: sortDirection,
                      })
                    }
                  />
                </div>
                <Button onClick={toggleIsSidebarOpen}>Filters</Button>
              </SearchBarContainer>
              <When condition={hasData}>
                <AccumulatedCostData
                  hidePeriod
                  period={period}
                  totalCost={Number(data?.cost?.currentMonthTotal)}
                  count={count}
                  monthlySavings={data?.savingsAmount?.total ?? "0"}
                  isLoading={loading}
                  forecast={data?.cost?.monthlyForecast ?? "0"}
                  averageCost={data?.cost?.currentMonthTotal}
                />
              </When>
              <div css={{ width: "100%" }}>
                <ReactTableV2
                  striped
                  sort
                  columns={columns}
                  data={rows}
                  headerBackground={theme.color.background.ui03}
                  defaultSort={defaultSort}
                  onSort={handleSorting}
                />
                <Pagination count={count} />
              </div>
            </FlexContainer>
          </LoaderWrap>
        </ErrorWrap>
      </EstateContainer>
      <FiltersSidebar
        hiddenFilters={["account", "provider"]}
        isSidebarOpen={isSidebarOpen}
        filters={filters}
        toggleIsSidebarOpen={toggleIsSidebarOpen}
        updateFilters={handleUpdate}
        toggleFilters={toggleFilters}
        clearFilters={clearFilters}
      />
    </>
  );
}
